/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, Sorting, SortDirection, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, IMemberSearchFilters } from 'app/cd/services/member-service';
import { LookupService } from 'app/core/services/lookup-service';
import { apiCall } from 'app/shared/lib/api/api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class AlternateAddresses extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    @bindable.boolean()
    public readonly: boolean = false;

    protected validateForm = validate;
    protected saveAddressIsValid: boolean = false;

    protected canAddAlternateAddress: boolean = false;

    protected types: Core.AddressType[] = [];
    protected numberOfTypes: number = 0;
    protected regions: ReadonlyArray<Core.AddressRegion> = [];

    protected editedAddress: CD.MemberAddress | null = null;

    protected sorting = new Sorting();
    protected alternateAddresses: CD.MemberAddress[] = [];


    ////****************** Element References ***************************************////
    private $saveAddressModal!: IPopup;
    protected $saveAddressForm!: IForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'type.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMemberAddresses();
        await this.loadLookups();
        this.canAddAlternateAddress = this.alternateAddresses.length < this.numberOfTypes;
    }


    ////****************** Functions ************************************************////
    protected closeSaveAddressModal(): void {
        if(this.$saveAddressForm.reset) {
            this.$saveAddressForm.reset();
        }
        this.$saveAddressModal.close();
    }

    protected async handleSaveAddressFormSuccess(): Promise<void> {
        if(this.$saveAddressForm.reset) {
            this.$saveAddressForm.reset();
        }
        this.$saveAddressModal.close();
        await this.reload();
    }

    protected saveAddress(address: CD.MemberAddress | null = null): void {
        this.editedAddress = address ?? {};
        this.$saveAddressModal.open();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberAddresses(): Promise<void> {
        if(!this.memberId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            includeInactive: true
        };

        const includes: string[] = [
            'type',
            'region'
        ];

        const searchFilters: IMemberSearchFilters = {
            alternateTypes: true
        }

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberAddresses(this.memberId, undefined, options, includes, [], searchFilters);
            this.alternateAddresses = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            const searchFilters: IMemberSearchFilters = {
                alternateTypes: true
            }

            const [regions, types] = await Promise.all(
                [
                    this.lookupService.loadStates(),
                    this.memberService.loadMemberAddressTypes({}, [], searchFilters),
                ]);

            this.regions = regions.results;
            this.types = types.results;
            this.numberOfTypes = this.types.length;
            // Remove types already in use
            this.alternateAddresses.forEach(address => {
                const indexToRemove = this.types.findIndex(type => type?.id === address?.type?.id)
                this.types.splice(indexToRemove, 1);
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateAddress(address: CD.MemberAddress): Promise<void> {
        if(!address.id) return;

        // Show confirmation dialog only if deactivating
        if(address.isActive) {
            const result = await confirmation.showDeactivate('cd:memberContact.address.alternate.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            const response = await this.memberService.activateMemberAddress(address.id, !address.isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.activate('cd:memberContact.address.alternate.$', false, false);
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:memberContact.address.alternate.$', !address.isActive, isSuccessful);
            await this.reload();
            this.state.endSave();
        }
    }
}
